var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/vim-connect-gql-client.ts
import { RetryLink } from "@apollo/client/link/retry";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloLink,
  from,
  Observable
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { calculateWidgetApiEnvUrl } from "@getvim-communication/management";
import { v4 as uuid } from "uuid";

// src/errors.ts
var NetworkError = class extends Error {
  constructor(message, correlationId) {
    super(message);
    this.name = this.constructor.name;
    this.correlationId = correlationId;
  }
};
var errors_default = NetworkError;

// src/vim-connect-gql-client.ts
import TimeoutLink from "apollo-link-timeout";
var RETRIES_ERRORS_WHITELIST_MUTATION = [
  "ECONNRESET",
  "ENOTFOUND",
  "EHOSTUNREACH",
  "ECONNREFUSED"
];
var RETRIES_ERRORS_WHITELIST = [
  "Failed to fetch",
  "socket hang up",
  "Received status code 404",
  ...RETRIES_ERRORS_WHITELIST_MUTATION
];
var NETWORK_ERRORS_WITHOUT_RETRIES = ["Timeout exceeded", "Received status code 403"];
var IS_NETWORK_ERROR_ERRORS = [...RETRIES_ERRORS_WHITELIST, ...NETWORK_ERRORS_WITHOUT_RETRIES];
function isNetworkError(error, errorsWhitelist) {
  return error && (errorsWhitelist.includes(error.message) || !!errorsWhitelist.find((errorCode) => error.code === errorCode || error.message.includes(errorCode)));
}
var checkOperationFailed = (error, operation) => {
  var _a, _b;
  const isBaking = (_a = error == null ? void 0 : error.bodyText) == null ? void 0 : _a.toLowerCase().includes("baking");
  const operationType = (_b = operation.query.definitions[0]) == null ? void 0 : _b.operation;
  const isQueryOperation = operationType === "query";
  const isMutationOperation = operationType === "mutation";
  return isQueryOperation && (isBaking || isNetworkError(error, RETRIES_ERRORS_WHITELIST)) || isMutationOperation && !isBaking && isNetworkError(error, RETRIES_ERRORS_WHITELIST_MUTATION);
};
var DEFAULT_RETRY_OPTIONS = {
  delay: {
    initial: 500,
    max: 2e3,
    jitter: true
  }
};
var VimConnectGqlClient = class extends ApolloClient {
  constructor({
    apiUrl = "/api/graphql",
    cache = new InMemoryCache(),
    accessTokenCallback,
    uploadLink,
    retryOnNetworkError = true,
    retryOptions = DEFAULT_RETRY_OPTIONS,
    additionalHeaders = {},
    logger,
    timeoutInMs = 4e4
  }) {
    retryOptions = __spreadValues({
      attempts: {
        max: 3,
        retryIf: (error, operation) => {
          const shouldRetry = checkOperationFailed(error, operation);
          if (shouldRetry) {
            const correlationId = operation.getContext().correlationId;
            logger == null ? void 0 : logger.warning("Retrying graphql request due network error", {
              error,
              operation,
              correlationId
            });
          }
          return shouldRetry;
        }
      }
    }, retryOptions);
    const terminatingLink = uploadLink || createHttpLink({ uri: apiUrl });
    const authLink = new ApolloLink((operation, forward) => {
      const correlationId = uuid();
      operation.setContext(({ headers = {} }) => ({
        headers: __spreadValues(__spreadValues(__spreadValues({
          authorization: this.accessToken ? this.accessToken : accessTokenCallback(),
          ["x-vim-correlation-id"]: correlationId
        }, this.deviceId ? { ["x-vim-device-id"]: this.deviceId } : void 0), headers), additionalHeaders),
        correlationId
      }));
      return forward(operation).map((response) => {
        const correlationId2 = operation.getContext().correlationId;
        if (correlationId2 && response.data)
          response.data.correlationId = correlationId2;
        return response;
      });
    });
    const executionTimeLink = new ApolloLink((operation, forward) => {
      operation.setContext({ start: performance.now() });
      return forward(operation).map((response) => {
        if (response.data)
          response.data.executionTime = (performance.now() - operation.getContext().start).toFixed(3);
        return response;
      });
    });
    const retryLink = new RetryLink(retryOptions);
    const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
      const networkErrorFromGqlError = graphQLErrors == null ? void 0 : graphQLErrors.find((element) => isNetworkError(element, IS_NETWORK_ERROR_ERRORS));
      if (networkErrorFromGqlError || isNetworkError(networkError, IS_NETWORK_ERROR_ERRORS)) {
        const message = (networkError == null ? void 0 : networkError.message) || (networkErrorFromGqlError == null ? void 0 : networkErrorFromGqlError.message) || "Unknown network error";
        return new Observable((observer) => {
          observer.error(new errors_default(message, operation.getContext().correlationId));
          forward(operation).subscribe({
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: () => {
              observer.complete.bind(observer)();
            }
          });
        });
      }
      return forward(operation);
    });
    const timeoutLink = new TimeoutLink(timeoutInMs);
    const links = [
      ...retryOnNetworkError ? [retryLink] : [],
      executionTimeLink,
      authLink,
      errorLink,
      timeoutLink,
      terminatingLink
    ];
    const link = from(links);
    const clientOptions = {
      link,
      cache,
      queryDeduplication: false
    };
    super(clientOptions);
  }
  static getHttpLinkUrl(apiUrl, widgetId) {
    const overrideEnvConfig = getOverrideWidgetsConfig();
    if (overrideEnvConfig) {
      const gqlServerBaseUrl = calculateWidgetApiEnvUrl(widgetId, overrideEnvConfig);
      if (gqlServerBaseUrl) {
        return `${gqlServerBaseUrl}${apiUrl}`;
      }
    }
    return apiUrl;
  }
  setAccessToken(token) {
    this.accessToken = token;
  }
  setDeviceId(deviceId) {
    this.deviceId = deviceId;
  }
  getDeviceId() {
    return this.deviceId;
  }
};
function getOverrideWidgetsConfig() {
  try {
    const overrideEnvConfig = new URL(window.location.href).searchParams.get("overrideEnvConfig");
    if (overrideEnvConfig) {
      return JSON.parse(overrideEnvConfig);
    }
  } catch (error) {
    console.log(`Failed to get overrideEnvConfig from url`, error);
  }
  return void 0;
}
export {
  DEFAULT_RETRY_OPTIONS,
  VimConnectGqlClient,
  checkOperationFailed
};
//# sourceMappingURL=index.js.map
